import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import type {
  OnboardingSeriesStatus,
  OnboardingState,
  OnboardingStepState,
  OnboardingStepStatus,
} from "@/api/models/onboarding";
import {
  arriveAtOnboardingStep,
  completeOnboarding as completeOnboardingApi,
  completeOnboardingStep,
  getOnboardingState,
  restartOnboarding as restartOnboardingApi,
  skipOnboardingStep,
  startOnboarding,
} from "@/api/services/onboardingService";
import { useApp } from "./AppContext";

// ── Types ─────────────────────────────────────────────────────────────────────

export type OnboardingStepId =
  | "general-settings"
  | "data-sources"
  | "roles-permissions"
  | "user-management"
  | "dashboard-layout";

/** Persisted step status — mirrors the OnboardingSteps.StepStatus column. */
export type StepStatus = OnboardingStepStatus;

export interface OnboardingStep {
  id: OnboardingStepId;
  number: number;
  name: string;
  sidebarDescription: string;
  cardDescription: string;
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "general-settings",
    number: 1,
    name: "General Settings",
    sidebarDescription: "Configure company & forecast defaults",
    cardDescription:
      "Configure your company, financial year and forecast defaults",
  },
  {
    id: "data-sources",
    number: 2,
    name: "Data Sources",
    sidebarDescription: "Connect your data sources",
    cardDescription: "Connect your accounting, payroll and banking systems",
  },
  {
    id: "roles-permissions",
    number: 3,
    name: "Roles & Permissions",
    sidebarDescription: "Configure roles for your team",
    cardDescription: "Set up and customise roles before assigning users",
  },
  {
    id: "user-management",
    number: 4,
    name: "User Management",
    sidebarDescription: "Invite your team",
    cardDescription: "Invite your team and assign roles",
  },
  {
    id: "dashboard-layout",
    number: 5,
    name: "Dashboard Layout",
    sidebarDescription: "Set default dashboard layouts",
    cardDescription: "Configure default card layouts for each role",
  },
];

export const WELCOME_STEP = 0;
export const COMPLETION_STEP = ONBOARDING_STEPS.length + 1;

// Demo mode mirrors the tenant-level model: one key for the whole tenant, never
// keyed by demo user, so switching demo user does not retrigger onboarding.
const DEMO_STATE_KEY = "onboarding_tenant_state";

export const ONBOARDING_QUERY_KEY = ["onboardingState"] as const;

// ── Tenant state transitions (shared by demo mode) ────────────────────────────

const EMPTY_STATE: OnboardingState = {
  seriesId: null,
  status: "NotStarted",
  startedDate: null,
  completedDate: null,
  restartCount: 0,
  steps: [],
};

const newSeriesSteps = (): OnboardingStepState[] =>
  ONBOARDING_STEPS.map((step) => ({
    stepNumber: step.number,
    stepName: step.name,
    stepStatus: "NotStarted" as OnboardingStepStatus,
    startedByUserId: null,
    startedAt: null,
    completedByUserId: null,
    completedDate: null,
  }));

const ensureSeries = (state: OnboardingState): OnboardingState =>
  state.seriesId === null || state.status === "NotStarted"
    ? {
        seriesId: crypto.randomUUID(),
        status: "InProgress",
        startedDate: new Date().toISOString(),
        completedDate: null,
        restartCount: state.restartCount,
        steps: newSeriesSteps(),
      }
    : state;

const mapStep = (
  state: OnboardingState,
  stepNumber: number,
  update: (step: OnboardingStepState) => OnboardingStepState,
): OnboardingState => ({
  ...state,
  steps: state.steps.map((step) =>
    step.stepNumber === stepNumber ? update(step) : step,
  ),
});

const arriveLocally = (
  state: OnboardingState,
  stepNumber: number,
  userId: string,
): OnboardingState =>
  mapStep(ensureSeries(state), stepNumber, (step) =>
    // Arrival only promotes NotStarted; decided steps are never downgraded.
    step.stepStatus === "NotStarted"
      ? {
          ...step,
          stepStatus: "Started",
          startedByUserId: userId,
          startedAt: new Date().toISOString(),
        }
      : step,
  );

const decideLocally = (
  state: OnboardingState,
  stepNumber: number,
  stepStatus: OnboardingStepStatus,
  userId: string,
): OnboardingState => {
  const now = new Date().toISOString();
  return mapStep(ensureSeries(state), stepNumber, (step) => ({
    ...step,
    stepStatus,
    startedByUserId: step.startedByUserId ?? userId,
    startedAt: step.startedAt ?? now,
    completedByUserId: userId,
    completedDate: now,
  }));
};

const completeSeriesLocally = (
  state: OnboardingState,
  userId: string,
): OnboardingState => {
  const now = new Date().toISOString();
  const withSeries = ensureSeries(state);
  return {
    ...withSeries,
    status: "Completed",
    completedDate: now,
    steps: withSeries.steps.map((step) =>
      step.stepStatus === "Completed"
        ? step
        : {
            ...step,
            stepStatus: "Skipped",
            completedByUserId: userId,
            completedDate: now,
          },
    ),
  };
};

const restartLocally = (state: OnboardingState): OnboardingState => ({
  seriesId: crypto.randomUUID(),
  status: "InProgress",
  startedDate: new Date().toISOString(),
  completedDate: null,
  restartCount: state.restartCount + 1,
  steps: newSeriesSteps(),
});

const readDemoState = (): OnboardingState => {
  try {
    const raw = localStorage.getItem(DEMO_STATE_KEY);
    return raw ? (JSON.parse(raw) as OnboardingState) : EMPTY_STATE;
  } catch {
    return EMPTY_STATE;
  }
};

const writeDemoState = (state: OnboardingState): void => {
  try {
    localStorage.setItem(DEMO_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore — demo persistence is best effort
  }
};

// ── Context shape ──────────────────────────────────────────────────────────────

interface OnboardingContextValue {
  // State
  isAssistantOpen: boolean;
  hasCompleted: boolean;
  hasDismissed: boolean;
  /** View/navigation state only: 0 = Welcome, 1-5 = steps, 6 = summary. */
  currentStep: number;
  status: OnboardingSeriesStatus;
  steps: OnboardingStepState[];
  restartCount: number;
  // Actions
  openAssistant: () => void;
  dismissOnboarding: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  restartOnboarding: () => void;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  arriveAtStep: (stepNumber: number) => void;
  completeCurrentStep: () => void;
  skipCurrentStep: () => void;
  getStepStatus: (stepId: OnboardingStepId) => StepStatus;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, mode } = useApp();
  const userId = currentUser.id;
  const isLive = mode === "live";

  const [demoState, setDemoState] = useState<OnboardingState>(() =>
    isLive ? EMPTY_STATE : readDemoState(),
  );
  const [currentStep, setCurrentStep] = useState<number>(WELCOME_STEP);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);

  // Onboarding state is read through GraphQL and always reread after a mutation so
  // concurrent administrators converge on the server's view (last write wins).
  const queryClient = useQueryClient();
  const { data: liveState } = useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: getOnboardingState,
    enabled: isLive,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // Mutations are serialised and each one invalidates the GraphQL read, so the wizard
  // and the summary can never show a status the server has already moved past.
  const writeQueue = useRef<Promise<unknown>>(Promise.resolve());

  const enqueueLiveMutation = useCallback(
    (
      mutate: () => Promise<void>,
      optimistic?: (previous: OnboardingState) => OnboardingState,
    ): Promise<unknown> => {
      // The same transitions demo mode uses are applied to the cached GraphQL result so
      // the wizard and the summary show the new status immediately; the invalidation that
      // follows replaces it with the server's version.
      if (optimistic) {
        queryClient.setQueryData<OnboardingState>(
          ONBOARDING_QUERY_KEY,
          (previous) => optimistic(previous ?? EMPTY_STATE),
        );
      }

      writeQueue.current = writeQueue.current
        .catch(() => undefined)
        .then(mutate)
        .catch((error: unknown) => {
          toast.error("Could not save your onboarding progress", {
            description:
              error instanceof Error ? error.message : "Please try again.",
          });
        })
        .then(() =>
          queryClient.invalidateQueries({
            queryKey: ONBOARDING_QUERY_KEY,
            refetchType: "all",
          }),
        );

      return writeQueue.current;
    },
    [queryClient],
  );

  // Demo state is rehydrated only when the mode actually changes — never on mount, where
  // it could clobber a series the gate has just created.
  const previousIsLive = useRef(isLive);

  useEffect(() => {
    if (previousIsLive.current === isLive) return;
    previousIsLive.current = isLive;

    if (!isLive) {
      setDemoState(readDemoState());
      setHasDismissed(false);
      setIsAssistantOpen(false);
    }
  }, [isLive]);

  const state: OnboardingState = isLive
    ? (liveState ?? EMPTY_STATE)
    : demoState;

  // Until the GraphQL read resolves, treat live onboarding as completed so the gate
  // does not flash open for tenants that have already finished.
  const hasCompleted = isLive
    ? liveState === undefined || liveState.status === "Completed"
    : demoState.status === "Completed";

  // localStorage is written outside the state updater: updaters must stay pure so they
  // remain safe to re-run.
  const applyDemo = useCallback(
    (transition: (previous: OnboardingState) => OnboardingState) => {
      setDemoState((previous) => transition(previous));
    },
    [],
  );

  useEffect(() => {
    if (!isLive) {
      writeDemoState(demoState);
    }
  }, [demoState, isLive]);

  const openAssistant = useCallback(() => {
    setIsAssistantOpen(true);
    if (isLive) {
      enqueueLiveMutation(startOnboarding, ensureSeries);
    } else {
      applyDemo((previous) => ensureSeries(previous));
    }
  }, [applyDemo, enqueueLiveMutation, isLive]);

  const arriveAtStep = useCallback(
    (stepNumber: number) => {
      if (isLive) {
        enqueueLiveMutation(
          () => arriveAtOnboardingStep(stepNumber),
          (previous) => arriveLocally(previous, stepNumber, userId),
        );
      } else {
        applyDemo((previous) => arriveLocally(previous, stepNumber, userId));
      }
    },
    [applyDemo, enqueueLiveMutation, isLive, userId],
  );

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(step);
      if (step >= 1 && step <= ONBOARDING_STEPS.length) {
        arriveAtStep(step);
      }
    },
    [arriveAtStep],
  );

  const nextStep = useCallback(() => {
    goToStep(Math.min(currentStep + 1, COMPLETION_STEP));
  }, [currentStep, goToStep]);

  const prevStep = useCallback(() => {
    goToStep(Math.max(currentStep - 1, 1));
  }, [currentStep, goToStep]);

  const completeCurrentStep = useCallback(() => {
    const stepNumber = currentStep;
    if (stepNumber < 1 || stepNumber > ONBOARDING_STEPS.length) return;

    if (isLive) {
      enqueueLiveMutation(
        () => completeOnboardingStep(stepNumber),
        (previous) => decideLocally(previous, stepNumber, "Completed", userId),
      );
    } else {
      applyDemo((previous) =>
        decideLocally(previous, stepNumber, "Completed", userId),
      );
    }
    nextStep();
  }, [applyDemo, currentStep, enqueueLiveMutation, isLive, nextStep, userId]);

  const skipCurrentStep = useCallback(() => {
    const stepNumber = currentStep;
    if (stepNumber < 1 || stepNumber > ONBOARDING_STEPS.length) return;

    if (isLive) {
      enqueueLiveMutation(
        () => skipOnboardingStep(stepNumber),
        (previous) => decideLocally(previous, stepNumber, "Skipped", userId),
      );
    } else {
      applyDemo((previous) =>
        decideLocally(previous, stepNumber, "Skipped", userId),
      );
    }
    nextStep();
  }, [applyDemo, currentStep, enqueueLiveMutation, isLive, nextStep, userId]);

  // Completing and dismissing both force-complete the series: every outstanding
  // step is recorded as an explicit skip.
  const forceComplete = useCallback(() => {
    if (isLive) {
      enqueueLiveMutation(completeOnboardingApi, (previous) =>
        completeSeriesLocally(previous, userId),
      );
    } else {
      applyDemo((previous) => completeSeriesLocally(previous, userId));
    }
    setIsAssistantOpen(false);
  }, [applyDemo, enqueueLiveMutation, isLive, userId]);

  const completeOnboarding = useCallback(() => {
    forceComplete();
  }, [forceComplete]);

  const dismissOnboarding = useCallback(() => {
    setHasDismissed(true);
    forceComplete();
  }, [forceComplete]);

  const restartOnboarding = useCallback(() => {
    if (isLive) {
      enqueueLiveMutation(restartOnboardingApi, restartLocally);
    } else {
      applyDemo((previous) => restartLocally(previous));
    }
    setHasDismissed(false);
    setCurrentStep(WELCOME_STEP);
    setIsAssistantOpen(true);
  }, [applyDemo, enqueueLiveMutation, isLive]);

  const getStepStatus = useCallback(
    (stepId: OnboardingStepId): StepStatus => {
      const definition = ONBOARDING_STEPS.find((s) => s.id === stepId);
      if (!definition) return "NotStarted";
      return (
        state.steps.find((s) => s.stepNumber === definition.number)
          ?.stepStatus ?? "NotStarted"
      );
    },
    [state.steps],
  );

  const value = useMemo(
    () => ({
      isAssistantOpen,
      hasCompleted,
      hasDismissed,
      currentStep,
      status: state.status,
      steps: state.steps,
      restartCount: state.restartCount,
      openAssistant,
      dismissOnboarding,
      completeOnboarding,
      resetOnboarding: restartOnboarding,
      restartOnboarding,
      goToStep,
      nextStep,
      prevStep,
      arriveAtStep,
      completeCurrentStep,
      skipCurrentStep,
      getStepStatus,
    }),
    [
      isAssistantOpen,
      hasCompleted,
      hasDismissed,
      currentStep,
      state.status,
      state.steps,
      state.restartCount,
      openAssistant,
      dismissOnboarding,
      completeOnboarding,
      restartOnboarding,
      goToStep,
      nextStep,
      prevStep,
      arriveAtStep,
      completeCurrentStep,
      skipCurrentStep,
      getStepStatus,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx)
    throw new Error("useOnboarding must be used within OnboardingProvider");
  return ctx;
};
